import { CancelTokenSource } from 'axios';

import {
  AuditRecord,
  SearchResult,
  TenderConfigOptions,
  UserRef,
} from '@rygen/rygen-ui/model';
import { Shipment as EsShipment } from '@rygen/tms-search-model/search/shipment';

import {
  AcceptRateQuoteRequest,
  BrokerageDates,
  CancelShipmentCommand,
  DocumentRequestResponse,
  DriverContact,
  ES_SHIPMENT_COLUMNS_SORT_FIELDS,
  HttpHeader,
  MacroPointStartTrackingResponse,
  Manifest,
  ManualShipment,
  Order,
  RateQuote,
  RateSelectionReason,
  RateShop,
  SearchOptions,
  Shipment,
  SHIPMENT_COLUMNS_SORT_FIELDS,
  ShipmentInviteCarriersToSpotMarketCommand,
  ShipmentSearchCriteria,
  ShipmentSearchResult,
  ShipmentStop,
  ShipmentTrackingResponse,
  SpotMarket,
  TenderAcceptRequest,
  TenderAuthorizationDecision,
  TenderRejectRequest,
  TenderRequest,
  TenderToCarrierRequest,
  TerminalInfo,
  UpdateExpectedArrivalRequest,
  WaterfallTender,
  WaterfallTenderConfig,
  WaterfallTenderInitiatedResponse,
} from '@/model';
import { ValidShipOrder } from '@/model/routeBuilder';

import { BaseHttpController } from './BaseHttpController';

const endpoint = 'shipment';

const DEFAULT_SEARCH_OPTIONS: SearchOptions = {
  useCache: true,
  useSelectedDomainContext: true,
};

export class ShipmentController extends BaseHttpController {
  constructor() {
    super(endpoint);
  }

  async create(shipment: ManualShipment): Promise<Shipment> {
    if (shipment.id !== undefined) {
      throw new Error('Id expected to be null when calling create');
    }

    const response = await this.http.post<Shipment>(
      'create',
      {
        shipment,
        // TODO: remove `skipAutoTender` from `ManualShipment` and pass it as a parameter
        skipAutoAction: shipment.skipAutoTender,
      },
      {
        headers: {
          [HttpHeader.SOURCE]: 'Shipment Entry',
        },
      },
    );
    return new Shipment(response.data);
  }

  async save(shipment: Shipment): Promise<Shipment> {
    const response = await this.http.post<Shipment>('', shipment);
    return new Shipment(response.data);
  }

  async findById(shipmentId: number): Promise<Shipment> {
    const response = await this.http.get<Shipment>(`${shipmentId}`);
    return new Shipment(response.data);
  }

  async findRelatedOrders(shipmentId: number): Promise<Order[]> {
    const response = await this.http.get<Order[]>(
      `${shipmentId}/related-orders`,
    );
    return response.data.map((val) => new Order(val));
  }

  async findAllRelatedShipOrders(
    shipmentId: number,
  ): Promise<ValidShipOrder[]> {
    if (!shipmentId) {
      throw new Error('Shipment Id is required.');
    }

    const response = await this.http.get<ValidShipOrder[]>(
      `${shipmentId}/ship-order`,
    );
    return response.data.map((val) => new ValidShipOrder(val));
  }

  async search(
    criteria: ShipmentSearchCriteria,
    opts: SearchOptions = DEFAULT_SEARCH_OPTIONS,
  ): Promise<ShipmentSearchResult> {
    const options: SearchOptions = { ...DEFAULT_SEARCH_OPTIONS, ...opts };
    criteria.referenceSort =
      criteria.sortField !== undefined &&
      !SHIPMENT_COLUMNS_SORT_FIELDS.includes(criteria.sortField);

    const response = await this.http.post<ShipmentSearchResult>(
      'search',
      criteria,
      {
        cancelToken: options.cancelToken?.token,
        params: {
          useCache: !!options.useCache,
          useSelectedDomainContext: !!options.useSelectedDomainContext,
        },
      },
    );
    return new ShipmentSearchResult(response.data);
  }

  async searchWithElasticSearch(
    criteria: ShipmentSearchCriteria,
    opts: SearchOptions = DEFAULT_SEARCH_OPTIONS,
  ): Promise<SearchResult<EsShipment>> {
    const options: SearchOptions = { ...DEFAULT_SEARCH_OPTIONS, ...opts };
    criteria.referenceSort =
      criteria.sortField !== undefined &&
      !ES_SHIPMENT_COLUMNS_SORT_FIELDS.includes(criteria.sortField);

    const response = await this.http.post<SearchResult<EsShipment>>(
      'query',
      criteria,
      {
        cancelToken: options.cancelToken?.token,
        params: {
          useCache: !!options.useCache,
          useSelectedDomainContext: !!options.useSelectedDomainContext,
        },
      },
    );
    return new SearchResult(response.data);
  }

  async update(shipment: Shipment): Promise<Shipment> {
    if (!shipment.id) {
      throw new Error('Id is required to perform update');
    }

    const response = await this.http.put<Shipment>('', { shipment });
    return new Shipment(response.data);
  }

  async updateTags(
    shipment: Shipment,
    tags: string[],
    cancelToken?: CancelTokenSource,
  ): Promise<Shipment> {
    if (!shipment.id) {
      throw new Error('Id is required to perform update');
    }

    const response = await this.http.put<Shipment>(
      'tags',
      { shipmentId: shipment.id, tags },
      { cancelToken: cancelToken?.token },
    );
    return new Shipment(response.data);
  }

  async updateAssignedUsers(
    shipmentId: number,
    assignedUsers: UserRef[],
  ): Promise<Shipment> {
    const response = await this.http.put<Shipment>('users', {
      shipmentId: shipmentId,
      users: assignedUsers,
    });

    return new Shipment(response.data);
  }

  async updateCarrierNotes(
    shipmentId: number,
    carrierNotes: string,
  ): Promise<Shipment> {
    const response = await this.http.put<Shipment>('carrier-notes', {
      shipmentId,
      carrierNotes,
    });

    return new Shipment(response.data);
  }

  async removeOrders(
    shipment: Shipment,
    orderIds: number[],
  ): Promise<Shipment> {
    if (!shipment.id) {
      throw new Error('Id is required to perform update');
    }

    const response = await this.http.put<Shipment>('remove-orders', {
      shipmentId: shipment.id,
      orderIds,
    });
    return new Shipment(response.data);
  }

  async removeLineItem(shipmentId: number, lineItemId: number): Promise<void> {
    await this.http.put<Shipment>('remove-line-item', {
      shipmentId: shipmentId,
      lineItemId: lineItemId,
    });
  }
  async getActiveTender(shipmentId: number): Promise<TenderRequest> {
    const response = await this.http.get<TenderRequest>(
      `${shipmentId}/tender/active`,
    );
    return new TenderRequest(response.data);
  }

  async getAllTenders(shipmentId: number): Promise<TenderRequest[]> {
    const response = await this.http.get<TenderRequest[]>(
      `${shipmentId}/tender/all`,
    );
    return response.data.map((val) => new TenderRequest(val));
  }

  async tenderToCarrier(
    shipmentId: number,
    tenderToCarrierRequest: TenderToCarrierRequest,
  ): Promise<TenderRequest> {
    const response = await this.http.post<TenderRequest>(
      `${shipmentId}/tender/send`,
      tenderToCarrierRequest,
    );
    return new TenderRequest(response.data);
  }

  async resendTender(
    shipmentId: number,
    tenderToCarrierRequest: TenderToCarrierRequest,
  ): Promise<TenderRequest> {
    const response = await this.http.post<TenderRequest>(
      `${shipmentId}/tender/resend`,
      tenderToCarrierRequest,
    );
    return new TenderRequest(response.data);
  }

  async tenderAccept(
    shipmentId: number,
    tenderAcceptRequest: TenderAcceptRequest,
  ): Promise<TenderRequest> {
    const response = await this.http.post<TenderRequest>(
      `${shipmentId}/tender/accept`,
      tenderAcceptRequest,
    );
    return new TenderRequest(response.data);
  }

  async tenderReject(
    shipmentId: number,
    tenderRejectRequest: TenderRejectRequest,
  ): Promise<TenderRequest> {
    const response = await this.http.post<TenderRequest>(
      `${shipmentId}/tender/reject`,
      tenderRejectRequest,
    );
    return new TenderRequest(response.data);
  }

  async loadRates(shipmentId: number): Promise<RateShop> {
    const response = await this.http.get<RateShop>(`${shipmentId}/rate`);
    return new RateShop(response.data);
  }

  async findSpotMarketsByRateShopId(
    shipmentId: number,
    rateShopId: number,
  ): Promise<SpotMarket[]> {
    const response = await this.http.get<SpotMarket[]>(
      `${shipmentId}/rate-shop/${rateShopId}/spot-market`,
    );
    return response.data.map((s) => new SpotMarket(s));
  }

  async generateRates(shipmentId: number): Promise<RateShop> {
    const response = await this.http.post<RateShop>(
      `${shipmentId}/rate/generate`,
    );
    return new RateShop(response.data);
  }

  async acceptRateQuote(
    shipmentId: number,
    acceptRateQuoteRequest: AcceptRateQuoteRequest,
  ): Promise<Shipment> {
    const response = await this.http.post<Shipment>(
      `${shipmentId}/rate/${acceptRateQuoteRequest.rateQuoteId}/accept`,
      acceptRateQuoteRequest,
    );
    return new Shipment(response.data);
  }

  async acceptBrokerage(
    shipmentId: number,
    rateQuoteId: number,
    tenderConfigOptions?: TenderConfigOptions,
  ): Promise<Shipment> {
    const response = await this.http.post<Shipment>(
      `${shipmentId}/rate/${rateQuoteId}/accept-brokerage`,
      { tenderConfigOptions },
    );
    return new Shipment(response.data);
  }

  async acceptRateQuoteAndTender(
    shipmentId: number,
    rateQuoteId: number,
    tenderToCarrierRequest: TenderToCarrierRequest,
  ): Promise<TenderRequest> {
    const response = await this.http.post<TenderRequest>(
      `${shipmentId}/rate/${rateQuoteId}/accept-and-tender`,
      tenderToCarrierRequest,
    );
    return new TenderRequest(response.data);
  }

  async initiateWaterfallTender(
    shipmentId: number,
    waterfallTenderConfig: WaterfallTenderConfig,
  ): Promise<WaterfallTenderInitiatedResponse> {
    const response = await this.http.post<WaterfallTenderInitiatedResponse>(
      `${shipmentId}/rate/waterfall-tender`,
      waterfallTenderConfig,
    );
    return {
      shipment: new Shipment(response.data.shipment),
      waterfallTender: new WaterfallTender(response.data.waterfallTender),
    };
  }

  async updateRateQuote(
    shipmentId: number,
    rateQuote: RateQuote,
  ): Promise<RateQuote> {
    if (!rateQuote.id) {
      throw new Error('rateQuote id is required to update rate quote');
    }
    const response = await this.http.put<RateQuote>(
      `${shipmentId}/rate/${rateQuote.id}`,
      rateQuote,
    );
    return new RateQuote(response.data);
  }

  async addSpotQuote(
    shipmentId: number,
    spotQuote: RateQuote,
  ): Promise<RateQuote> {
    const response = await this.http.post<RateQuote>(
      `${shipmentId}/rate/spot-quote`,
      spotQuote,
    );
    return new RateQuote(response.data);
  }

  async addLaneQuote(
    shipmentId: number,
    laneQuote: RateQuote,
  ): Promise<RateQuote> {
    const response = await this.http.post<RateQuote>(
      `${shipmentId}/rate/lane-quote`,
      laneQuote,
    );
    return new RateQuote(response.data);
  }

  async addBrokerage(
    shipmentId: number,
    brokerageQuote: RateQuote,
    brokerageDates?: BrokerageDates,
    rateSelectionReason?: RateSelectionReason,
  ): Promise<Shipment> {
    const response = await this.http.post<Shipment>(
      `${shipmentId}/rate/broker`,
      {
        shipmentId,
        brokerageQuote,
        brokerageDates,
        rateSelectionReason,
      },
    );
    return new Shipment(response.data);
  }

  async deleteBrokerage(
    shipmentId: number,
    brokerageId: number,
  ): Promise<void> {
    await this.http.delete<void>(`${shipmentId}/rate/broker/${brokerageId}`);
  }

  async updateBrokerage(
    shipmentId: number,
    brokerageQuote: RateQuote,
    brokerageDates?: BrokerageDates,
  ): Promise<RateQuote> {
    if (!brokerageQuote.id) {
      throw new Error('brokerage id is required to update brokerage quote');
    }
    const response = await this.http.put<RateQuote>(
      `${shipmentId}/rate/broker/${brokerageQuote.id}`,
      { shipmentId, brokerageQuote, brokerageDates },
    );
    return new RateQuote(response.data);
  }

  async deleteSpotQuote(
    shipmentId: number,
    spotQuoteId: number,
  ): Promise<void> {
    await this.http.delete<void>(
      `${shipmentId}/rate/spot-quote/${spotQuoteId}`,
    );
  }

  async carrierUnassign(shipmentId: number): Promise<Shipment> {
    const response = await this.http.post<Shipment>(
      `${shipmentId}/carrier/unassign`,
    );
    return new Shipment(response.data);
  }

  async cancel(
    shipmentId: number,
    cancelShipmentCommand: CancelShipmentCommand,
  ): Promise<Shipment> {
    const response = await this.http.post<Shipment>(
      `${shipmentId}/cancel`,
      cancelShipmentCommand,
    );
    return new Shipment(response.data);
  }

  async findShipmentAudits(shipmentId: number): Promise<AuditRecord[]> {
    const response = await this.http.get<AuditRecord[]>(`${shipmentId}/audit`);
    return response.data.map((val) => new AuditRecord(val));
  }

  async getTerminalInformation(shipmentId: number): Promise<TerminalInfo> {
    const response = await this.http.get<TerminalInfo>(
      `${shipmentId}/terminal-info`,
    );
    return new TerminalInfo(response.data);
  }

  async requestP44Tracking(
    shipmentId: number,
  ): Promise<ShipmentTrackingResponse> {
    const response = await this.http.get<ShipmentTrackingResponse>(
      `${shipmentId}/request-tracking`,
    );
    return new ShipmentTrackingResponse(response.data);
  }

  async requestApiTracking(shipmentId: number): Promise<void> {
    await this.http.get<void>(`${shipmentId}/request-api-tracking`);
  }

  async requestDocuments(
    shipmentId: number,
  ): Promise<DocumentRequestResponse[]> {
    const response = await this.http.get<DocumentRequestResponse[]>(
      `${shipmentId}/request-documents`,
    );
    return response.data.map((d) => new DocumentRequestResponse(d));
  }

  async findAllKnownServices(): Promise<string[]> {
    const response = await this.http.get<string[]>('service');
    return response.data;
  }

  async findActiveWaterfallTender(
    shipmentId: number,
  ): Promise<WaterfallTender | undefined> {
    const response = await this.http.get<WaterfallTender | undefined>(
      `${shipmentId}/rate/waterfall-tender/active`,
    );

    if (!response.data) {
      return undefined;
    }

    return new WaterfallTender(response.data);
  }

  async cancelActiveWaterfallTender(shipmentId: number): Promise<Shipment> {
    const response = await this.http.delete<Shipment>(
      `${shipmentId}/rate/waterfall-tender/active`,
    );

    return new Shipment(response.data);
  }

  async requestMacroPointTracking(shipmentId: number): Promise<void> {
    await this.http.get<void>(`${shipmentId}/macropoint/start-tracking`);
  }

  async checkRequirementsAndStartMacroPointTracking(
    shipmentId: number,
  ): Promise<MacroPointStartTrackingResponse> {
    const response = await this.http.get<MacroPointStartTrackingResponse>(
      `${shipmentId}/macropoint/check-requirements-start-tracking`,
    );
    return new MacroPointStartTrackingResponse(response.data);
  }

  async loadDriverContactForShipment(
    shipmentId: number,
  ): Promise<DriverContact> {
    const response = await this.http.get<DriverContact>(
      `${shipmentId}/driver-contact`,
    );
    return new DriverContact(response.data);
  }

  async createDriverContactForShipment(
    shipmentId: number,
    driverContact: DriverContact,
  ): Promise<DriverContact> {
    if (driverContact.shipmentId !== shipmentId) {
      throw new Error(
        'driverContact.shipment should be the same as shipmentId',
      );
    }

    const response = await this.http.post<DriverContact>(
      `${shipmentId}/driver-contact`,
      {
        shipmentId,
        driverContact,
      },
    );
    return new DriverContact(response.data);
  }

  async updateDriverContactForShipment(
    shipmentId: number,
    driverContact: DriverContact,
  ): Promise<DriverContact> {
    if (!driverContact.id) {
      throw new Error('Id is required to perform update');
    }

    if (driverContact.shipmentId !== shipmentId) {
      throw new Error(
        'driverContact.shipment should be the same as shipmentId',
      );
    }

    const response = await this.http.put<DriverContact>(
      `${shipmentId}/driver-contact/${driverContact.id}`,
      { shipmentId, driverContact },
    );
    return new DriverContact(response.data);
  }

  async updateExpectedArrival(
    shipmentId: number,
    updateRequest: UpdateExpectedArrivalRequest,
  ): Promise<ShipmentStop> {
    const shipmentStopId = updateRequest.relatedStop?.id;
    if (!shipmentStopId || shipmentStopId === -1) {
      throw new Error('Related stop id is required');
    }

    const response = await this.http.put<ShipmentStop>(
      `${shipmentId}/stop/${shipmentStopId}/expected-arrival`,
      {
        shipmentId,
        shipmentStopId,
        expectedArrival: updateRequest.expectedArrival,
        comment: updateRequest.comment,
      },
    );

    return new ShipmentStop(response.data);
  }

  async inviteCarriersToSpotMarket(
    shipmentId: number,
    command: ShipmentInviteCarriersToSpotMarketCommand,
  ): Promise<SpotMarket[]> {
    const response = await this.http.post<SpotMarket[]>(
      `${shipmentId}/spot-market/invite`,
      command,
    );
    return response.data.map((s) => new SpotMarket(s));
  }

  async loadSpotMarket(
    shipmentId: number,
    carrierId: number,
  ): Promise<SpotMarket> {
    const response = await this.http.get<SpotMarket>(
      `${shipmentId}/spot-market`,
      { params: { carrierId } },
    );
    return new SpotMarket(response.data);
  }

  async cancelSpotMarket(
    shipmentId: number,
    spotMarketId: number,
  ): Promise<void> {
    await this.http.post<void>(
      `${shipmentId}/spot-market/${spotMarketId}/cancel`,
    );
  }

  async resendSpotMarket(
    shipmentId: number,
    spotMarketId: number,
  ): Promise<void> {
    await this.http.post<void>(
      `${shipmentId}/spot-market/${spotMarketId}/resend`,
    );
  }

  async rejectSpotMarket(
    shipmentId: number,
    spotMarketId: number,
  ): Promise<void> {
    await this.http.post<void>(
      `${shipmentId}/spot-market/${spotMarketId}/reject`,
    );
  }

  async logFirstSpotMarketVisit(
    shipmentId: number,
    spotMarketId: number,
  ): Promise<void> {
    await this.http.post<void>(
      `${shipmentId}/spot-market/${spotMarketId}/first-visit`,
    );
  }

  async addSpotMarketBid(
    shipmentId: number,
    spotMarketId: number,
    spotMarketBid: RateQuote,
  ): Promise<RateQuote> {
    const response = await this.http.post<RateQuote>(
      `${shipmentId}/spot-market/${spotMarketId}/bid`,
      spotMarketBid,
    );
    return new RateQuote(response.data);
  }

  async deleteSpotMarketBid(
    shipmentId: number,
    spotMarketId: number,
    bidId: number,
  ): Promise<void> {
    await this.http.delete<void>(
      `${shipmentId}/spot-market/${spotMarketId}/bid/${bidId}`,
    );
  }

  async authorizeTender(
    shipmentId: number,
    rateQuoteId: number,
  ): Promise<TenderAuthorizationDecision> {
    const response = await this.http.get<TenderAuthorizationDecision>(
      `${shipmentId}/tender-authorization/${rateQuoteId}`,
    );
    return response.data;
  }

  async getActiveManifest(shipmentId: number): Promise<Manifest> {
    const response = await this.http.get<Manifest>(`${shipmentId}/manifest`);
    return new Manifest(response.data);
  }
}
